import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import PageHero from '../components/layout/PageHero';
import SEO from '../components/SEO';
import { useState, useEffect } from 'react';
import RichTextRenderer from '../components/content/RichTextRenderer';
import './Issues.css';



export default function IssueArticle() {
    const { id } = useParams();
    const [issue, setIssue] = useState(null);
    const [issuesIndex, setIssuesIndex] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const timestamp = Date.now();
                // Load index for navigation
                const indexRes = await fetch(`/data/issues_index.json?v=${timestamp}`);
                const index = await indexRes.json();
                setIssuesIndex(index);

                // Check if it's a link type first
                const issueMeta = index.issues.find(i => i.id === id);
                if (!issueMeta) {
                    setError('Issue not found');
                    setLoading(false);
                    return;
                }

                if (issueMeta.type === 'link') {
                    setIssue(issueMeta); // Will trigger redirect in render
                    setLoading(false);
                    return;
                }

                // Load content
                const detailRes = await fetch(`/data/issues/details/${id}.json?v=${timestamp}`);
                if (!detailRes.ok) throw new Error(`Issue content failed to load: ${detailRes.status}`);
                const detail = await detailRes.json();
                setIssue(detail);
                setLoading(false);
            } catch (err) {
                console.error("Failed to load issue", err);
                setError(err.message);
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (loading) return <div style={{ textAlign: 'center', padding: '10vh', color: '#fff' }}>Loading Fact File...</div>;
    if (error || !issue) return <Navigate to="/issues" replace />;

    if (issue.type === 'link') {
        return <Navigate to={issue.linkTarget} replace />;
    }

    return (
        <div className="issue-article-page">
            <SEO
                title={`${issue.title} - SAFECARE`}
                description={issue.summary}
                image={issue.image}
            />
            <PageHero
                title={issue.title}
                subtitle="Issue Briefing"
                image={issue.image}
                badge="The Breakdown"
            />

            <div className="container article-container">
                <motion.div
                    className="article-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="article-headline">"{issue.headline}"</h2>

                    <div className="article-body">
                        <RichTextRenderer content={issue.content} references={issue.references} />
                    </div>

                    {issue.references && (
                        <div className="article-references">
                            <h3>References</h3>
                            <ol>
                                {issue.references.map((ref) => (
                                    <li key={ref.id} id={`ref-${ref.id}`}>
                                        <a href={ref.url} target="_blank" rel="noopener noreferrer">
                                            {ref.text}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    <div className="article-navigation-footer">
                        {(() => {
                            if (!issuesIndex) return null;
                            const currentIndex = issuesIndex.issues.findIndex(i => i.id === id);
                            const nextIssue = issuesIndex.issues[(currentIndex + 1) % issuesIndex.issues.length];
                            return (
                                <Link to={nextIssue.type === 'link' ? nextIssue.linkTarget : `/issues/${nextIssue.id}`} className="next-topic-card glass-panel">
                                    <div className="next-label">Next Topic</div>
                                    <div className="next-title">{nextIssue.title}</div>
                                    <div className="next-arrow"><ArrowRight size={20} /></div>
                                </Link>
                            );
                        })()}
                    </div>

                    <Link to="/issues" className="back-link">
                        <ArrowLeft size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '5px' }} />
                        Back to Issues
                    </Link>
                </motion.div>
            </div>
        </div>
    );
}
